import React from 'react';
import { fmtMoney } from '../../utils/priceMath';
import { TM_REASON_MIN, extraHoursStations, newExtraInput, rateHint, reasonOk, toCents } from '../../utils/tmPricing';

const KIND_LABEL = {
  engineering: 'Engineering',
  programming: 'Programming',
  setup: 'Setup',
  extra_hours: 'Extra station hours',
  material_adjustment: 'Material adjustment',
  value_transfer: 'Value transfer',
  minimum_charge: 'Order minimum',
};
const TIMED = ['engineering', 'programming', 'setup'];

/**
 * Extras on an actuals-priced T&M order (decision 6), each with a reason: engineering / programming /
 * setup time (hours × a rate prefilled from the quote's fabrication rate, or an amount), extra
 * station hours at that station's quote rate (a station with no rate can't be picked), a signed
 * material adjustment, and a value transfer out of a line that bills 0 pieces. Target one billing
 * line, or spread over the billing lines. Separately, the single ORDER MINIMUM: a floor, not an
 * addition (decision 11). Money and hours are typed as text and sent as strings.
 *
 * rows / onChange(rows): the extra rows; minimum / onMinimum({amount, reason}); lines: the actuals
 * lines; previewExtras: the last current Preview's proposal.extras (what each extra came to);
 * previewLines: that Preview's proposal lines by so_line_id (a 'tm_manual' line bills none of its
 * share); ownPriceLines: the so_line_ids (strings) that have a unit of their own typed.
 */
export default function TmExtrasCard({
  rows = [], onChange, minimum = { amount: '', reason: '' }, onMinimum, lines = [], rates = {}, labelOf,
  previewExtras = null, previewLines = null, ownPriceLines = null, disabled = false,
}) {
  const billing = lines.filter((l) => l.supported !== false && Number(l.qty && l.qty.billable) > 0);
  // A value transfer can only move value a priced 0-bill line still has.
  const zeroBill = lines.filter((l) => l.supported !== false && Number(l.qty && l.qty.billable) === 0
    && (toCents((l.value && l.value.V) ?? '0') || 0) > 0);
  const ownPrice = new Set((ownPriceLines || []).map(String));
  const notBilledOn = (sid) => {
    const pl = previewLines ? previewLines[sid] : null;
    return !!pl && pl.price_source === 'tm_manual';
  };
  const stations = extraHoursStations(rates);
  const label = (l) => (labelOf ? labelOf(l) : `line ${l.so_line_id}`);
  const lineLabelById = (sid) => {
    const l = lines.find((x) => String(x.so_line_id) === String(sid));
    return l ? label(l) : `line ${sid}`;
  };
  const set = (key, patch) => onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const add = (kind, patch = {}) => onChange([...rows, newExtraInput(kind, TIMED.includes(kind) ? { rate: rateHint((rates || {}).fabrication_rate), ...patch } : patch)]);
  const remove = (key) => onChange(rows.filter((r) => r.key !== key));

  const input = (r, field, lab, extra = {}) => (
    <div className="col-auto" key={field}>
      <label className="form-label small mb-0" htmlFor={`tm-x-${r.key}-${field}`}>{lab}</label>
      <input
        id={`tm-x-${r.key}-${field}`}
        type="text"
        inputMode="decimal"
        className="form-control form-control-sm text-end"
        style={{ width: extra.width || 100 }}
        value={r[field]}
        disabled={disabled}
        onChange={(e) => set(r.key, { [field]: e.target.value })}
      />
    </div>
  );

  return (
    <div className="card mb-2" data-testid="tm-extras">
      <div className="card-header py-1 small fw-bold">Extras</div>
      <div className="card-body p-2 small">
        <div className="d-flex flex-wrap gap-1 mb-2">
          {TIMED.map((k) => (
            <button key={k} type="button" className="btn btn-sm btn-outline-secondary" disabled={disabled} onClick={() => add(k)}>+ {KIND_LABEL[k]}</button>
          ))}
          <button type="button" className="btn btn-sm btn-outline-secondary" disabled={disabled} onClick={() => add('extra_hours')}>+ {KIND_LABEL.extra_hours}</button>
          <button type="button" className="btn btn-sm btn-outline-secondary" disabled={disabled} onClick={() => add('material_adjustment')}>+ {KIND_LABEL.material_adjustment}</button>
          <button type="button" className="btn btn-sm btn-outline-secondary" disabled={disabled || zeroBill.length === 0}
            title={zeroBill.length === 0 ? 'Only a line that bills 0 pieces can pass its value on' : 'Move the value of a line that bills 0 pieces'}
            onClick={() => add('value_transfer')}>+ {KIND_LABEL.value_transfer}</button>
        </div>
        {rows.length === 0 && <div className="text-muted mb-2">No extras.</div>}
        {rows.map((r, i) => {
          const n = i + 1;
          return (
            <div key={r.key} className="border rounded p-2 mb-2" data-testid={`tm-extra-${n}`}>
              <div className="d-flex align-items-center gap-2 mb-1">
                <strong>{n}. {KIND_LABEL[r.kind] || r.kind}</strong>
                {TIMED.includes(r.kind) && (
                  <select className="form-select form-select-sm" style={{ width: 170 }} aria-label={`Extra ${n} hours or amount`} value={r.mode} disabled={disabled}
                    onChange={(e) => set(r.key, { mode: e.target.value })}>
                    <option value="hours">hours × rate</option>
                    <option value="amount">an amount</option>
                  </select>
                )}
                <button type="button" className="btn btn-sm btn-link text-danger ms-auto" disabled={disabled} onClick={() => remove(r.key)} aria-label={`Remove extra ${n}`}>Remove</button>
              </div>
              <div className="row g-2 align-items-end">
                {TIMED.includes(r.kind) && r.mode !== 'amount' && [
                  input(r, 'hours', 'Hours'),
                  input(r, 'rate', 'Rate $/h (quote fab rate)', { width: 150 }),
                ]}
                {TIMED.includes(r.kind) && r.mode === 'amount' && input(r, 'amount', 'Amount')}
                {r.kind === 'extra_hours' && (
                  <div className="col-auto">
                    <label className="form-label small mb-0" htmlFor={`tm-x-${r.key}-station`}>Station</label>
                    <select id={`tm-x-${r.key}-station`} className="form-select form-select-sm" value={r.station} disabled={disabled}
                      onChange={(e) => set(r.key, { station: e.target.value })}>
                      <option value="">— pick —</option>
                      {stations.map((s) => (
                        <option key={s.station} value={s.station} disabled={!s.ok}>
                          {s.station}{s.ok ? ` (${fmtMoney(s.rate)}/h)` : ' (no rate on the quote)'}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {r.kind === 'extra_hours' && input(r, 'hours', 'Hours')}
                {r.kind === 'material_adjustment' && input(r, 'amount', 'Amount (− = credit)', { width: 130 })}
                {r.kind === 'value_transfer' && (
                  <div className="col-auto">
                    <label className="form-label small mb-0" htmlFor={`tm-x-${r.key}-from`}>From (bills 0 pieces)</label>
                    <select id={`tm-x-${r.key}-from`} className="form-select form-select-sm" value={r.from_so_line_id} disabled={disabled}
                      onChange={(e) => set(r.key, { from_so_line_id: e.target.value })}>
                      <option value="">— pick —</option>
                      {zeroBill.map((l) => <option key={l.so_line_id} value={l.so_line_id}>{label(l)} ({fmtMoney(l.value && l.value.V)})</option>)}
                    </select>
                  </div>
                )}
                {r.kind === 'value_transfer' && input(r, 'amount', 'Amount')}
                <div className="col-auto">
                  <label className="form-label small mb-0" htmlFor={`tm-x-${r.key}-line`}>{r.kind === 'value_transfer' ? 'To' : 'On'}</label>
                  <select id={`tm-x-${r.key}-line`} className="form-select form-select-sm" value={r.so_line_id} disabled={disabled}
                    onChange={(e) => set(r.key, { so_line_id: e.target.value })}>
                    <option value="">Spread over the billing lines</option>
                    {billing.map((l) => <option key={l.so_line_id} value={l.so_line_id}>{label(l)}</option>)}
                  </select>
                </div>
                <div className="col">
                  <label className="form-label small mb-0" htmlFor={`tm-x-${r.key}-reason`}>Reason (at least {TM_REASON_MIN} characters)</label>
                  <input id={`tm-x-${r.key}-reason`} type="text" className={`form-control form-control-sm ${reasonOk(r.reason) ? 'is-valid' : ''}`}
                    value={r.reason} disabled={disabled} onChange={(e) => set(r.key, { reason: e.target.value })} />
                </div>
              </div>
              {r.so_line_id !== '' && r.so_line_id != null && ownPrice.has(String(r.so_line_id)) && (
                <div className="text-warning-emphasis mt-1">
                  This extra is not billed: {lineLabelById(r.so_line_id)} has a price of your own (the typed unit is its whole price).
                </div>
              )}
            </div>
          );
        })}
        <div className="border rounded p-2 mb-2" data-testid="tm-minimum">
          <strong>Order minimum (a floor, not an addition)</strong>
          <div className="text-muted mb-1">
            If the lines come to less, the difference is added and spread over the billing lines; an order already above it is unchanged.
          </div>
          <div className="row g-2 align-items-end">
            <div className="col-auto">
              <label className="form-label small mb-0" htmlFor="tm-min-amount">Minimum</label>
              <input id="tm-min-amount" type="text" inputMode="decimal" className="form-control form-control-sm text-end" style={{ width: 120 }}
                placeholder="none" value={minimum.amount} disabled={disabled} onChange={(e) => onMinimum({ ...minimum, amount: e.target.value })} />
            </div>
            <div className="col">
              <label className="form-label small mb-0" htmlFor="tm-min-reason">Reason (at least {TM_REASON_MIN} characters)</label>
              <input id="tm-min-reason" type="text" className={`form-control form-control-sm ${reasonOk(minimum.reason) ? 'is-valid' : ''}`}
                value={minimum.reason} disabled={disabled} onChange={(e) => onMinimum({ ...minimum, reason: e.target.value })} />
            </div>
          </div>
        </div>
        <div className="text-muted">A unit price you type on a line replaces that line&apos;s share of the extras (and of the minimum&apos;s top-up): the typed unit is the whole price of the line.</div>
        {Array.isArray(previewExtras) && previewExtras.length > 0 && (
          <table className="table table-sm mt-2 mb-0" data-testid="tm-extras-preview">
            <thead className="table-light">
              <tr><th>Extra (last Preview)</th><th className="text-end">Amount</th><th className="text-end">Applied</th><th>Allocation</th></tr>
            </thead>
            <tbody>
              {previewExtras.map((e, i) => (
                <tr key={i}>
                  <td>
                    {KIND_LABEL[e.kind] || e.kind}{e.station ? ` · ${e.station}` : ''}{e.hours != null ? ` · ${e.hours} h${e.rate != null ? ` × ${fmtMoney(e.rate)}` : ''}` : ''}
                    <div className="text-muted" style={{ fontSize: '0.7rem' }}>{e.reason}</div>
                  </td>
                  <td className="text-end">{fmtMoney(e.amount)}</td>
                  <td className="text-end">
                    {fmtMoney(e.applied)}
                    {e.kind === 'minimum_charge' && e.value_before != null && <div className="text-muted" style={{ fontSize: '0.7rem' }}>lines were {fmtMoney(e.value_before)}</div>}
                  </td>
                  <td>
                    {(e.allocation || []).length === 0 ? <span className="text-muted">nothing (already above)</span>
                      : (e.allocation || []).map(([sid, amt]) => (
                        <div key={sid}>
                          {lineLabelById(sid)}: {amt}
                          {notBilledOn(sid) && toCents(amt) !== 0 && <span className="text-warning-emphasis"> (not billed: a price of your own)</span>}
                        </div>
                      ))}
                    {e.spread && <div className="text-muted" style={{ fontSize: '0.7rem' }}>spread by {String(e.spread).replace(/_/g, ' ')}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
